import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Button } from '@Sage/hoi-poi-ui';

import getWillBeCleared from '../EntityCrud/utils/getWillBeCleared';
import FieldWrapper from './FieldWrapper';
import SelectFuzzy from './SelectFuzzy';

import './styles/multipleFuzzySingle.scss';

const MultipleFuzzySingle = memo(
    ({
        fieldId,
        inputs = [],
        labelMode,
        data = {},
        onChange,
        onClear,
        getError,
        errors = {},
        firstErrorField,
        isBulkAction,
        hidden,
        readOnly,
        labelButton,
        max = 10,
        disableButtonOnBulk,
        parentField,
        parentFieldForContext,
        parentFieldForContext2,
        parentValue,
        menuProps,
        getFieldsActions,
        fieldsToClear = {},
        smartFieldOverridesCache,
        environmentField,
    }) => {
        const containerRef = useRef(null);
        const hasInitializedRef = useRef(false);
        const dataRef = useRef(data);
        const innerInputsRef = useRef([]);

        const [number, setNumber] = useState(1);
        const [resetKeys, setResetKeys] = useState({});

        // Filter visible inputs - always synced with props
        const innerInputs = useMemo(() => {
            return inputs
                .map((input) => {
                    const smartFieldOverrides = smartFieldOverridesCache?.get(input.id) || {};
                    const { defaultValue, ...otherOverrides } = smartFieldOverrides;

                    return {
                        ...input,
                        ...otherOverrides,
                    };
                })
                .filter((input) => !input.hidden);
        }, [inputs, smartFieldOverridesCache]);

        // Keep refs in sync for stable callbacks
        dataRef.current = data;
        innerInputsRef.current = innerInputs;

        // Calculate required number based on data
        useEffect(() => {
            if (!innerInputs.length) return;

            // Count fields with data
            let counter = 0;
            innerInputs.forEach((current, index) => {
                if (data[current.id] || index === 0) counter++;
            });

            const mandatoryFields = innerInputs.filter((input) => input.mandatory);
            const requiredNumber = Math.max(counter, mandatoryFields.length, 1);

            // Only update if greater than current (don't reduce when data changes)
            // Or if this is the first real initialization with data
            if (!hasInitializedRef.current || requiredNumber > number) {
                hasInitializedRef.current = true;
                setNumber(requiredNumber);
            }
        }, [data, innerInputs, number]);

        // Scroll to first error field
        useEffect(() => {
            if (!firstErrorField || !containerRef.current) return;

            const hasMatchingError = innerInputs.some(
                (input) => input.id.toLowerCase() === firstErrorField.toLowerCase(),
            );

            if (hasMatchingError) {
                containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, [firstErrorField, innerInputs]);

        const handleAdd = useCallback(() => {
            setNumber((prev) => prev + 1);
        }, []);

        const handleOnChange = useCallback(
            (fieldIdToChange) => {
                return (item) => {
                    // Use refs for current values to avoid recreating callback
                    const currentData = dataRef.current;
                    const currentInputs = innerInputsRef.current;

                    // Check if value already exists in another field
                    const isRepeated = currentInputs.some(
                        (input) =>
                            input.id !== fieldIdToChange &&
                            item?.value &&
                            currentData[input.id]?.value &&
                            currentData[input.id].value === item.value,
                    );

                    if (isRepeated) {
                        // Force re-mount of SelectFuzzy to reset its internal state
                        setResetKeys((prev) => ({
                            ...prev,
                            [fieldIdToChange]: (prev[fieldIdToChange] || 0) + 1,
                        }));
                        return;
                    }

                    onChange?.(fieldIdToChange)?.(item);
                };
            },
            [onChange],
        );

        const addButton = useMemo(() => {
            if (number >= max) return null;
            if (number === innerInputs.length) return null;
            if (isBulkAction && disableButtonOnBulk) return null;

            return (
                <div className="add-button-container">
                    <Button type="terciary" onClick={handleAdd}>
                        {labelButton}
                    </Button>
                </div>
            );
        }, [
            number,
            innerInputs.length,
            labelButton,
            max,
            isBulkAction,
            disableButtonOnBulk,
            handleAdd,
        ]);

        const handleReset = useCallback(
            (fieldIdToReset) => () => {
                onChange?.(fieldIdToReset)?.(null);
            },
            [onChange],
        );

        const handleClear = useCallback(
            (field) => (shouldClear) => {
                onClear?.(field)?.(shouldClear);
            },
            [onClear],
        );

        const fields = useMemo(() => {
            return innerInputs
                .filter((current, index) => {
                    if (current.mandatory) return true;
                    if (data[current.id] || index < number) return true;
                    return false;
                })
                .map((field) => {
                    let newMenuProps = { ...(menuProps || {}) };

                    if (field?.inputAttrs?.list && getFieldsActions) {
                        const newActions = getFieldsActions({
                            list: field?.inputAttrs?.list,
                            fieldId: field.id,
                            type: field.type,
                            parentFieldForContext,
                            parentFieldForContext2,
                        });
                        newMenuProps.actions = [
                            ...(newMenuProps?.actions || []),
                            ...(newActions || []),
                        ];
                    }

                    const fieldCanClear = !field.readOnly && !field.mandatory;
                    const willBeCleared = getWillBeCleared(field);
                    const toDelete = !!fieldsToClear[field.id];
                    const fieldError = getError?.(errors[field.id], [field.dataLength]);

                    return (
                        <FieldWrapper
                            key={field.id}
                            canClear={fieldCanClear}
                            label={field.label}
                            labelMode={labelMode}
                            mandatory={field.mandatory}
                            description={field.description}
                            error={fieldError}
                            isBulkAction={isBulkAction}
                            onClear={handleClear(field)}
                            onReset={handleReset(field.id)}
                            toDelete={toDelete}
                            willBeCleared={willBeCleared}
                        >
                            <SelectFuzzy
                                key={`${field.id}-${resetKeys[field.id] || 0}`}
                                fieldId={field.id}
                                value={data[field.id]}
                                onChange={handleOnChange(field.id)}
                                parentField={parentField}
                                parentValue={parentValue}
                                {...field.inputAttrs}
                                menuProps={newMenuProps}
                                actions={newMenuProps.actions}
                                readOnly={readOnly || field.readOnly}
                                environmentField={environmentField}
                                error={!!fieldError}
                            />
                        </FieldWrapper>
                    );
                });
        }, [
            data,
            environmentField,
            errors,
            fieldsToClear,
            getError,
            getFieldsActions,
            handleClear,
            handleOnChange,
            handleReset,
            innerInputs,
            isBulkAction,
            labelMode,
            menuProps,
            number,
            parentField,
            parentFieldForContext,
            parentFieldForContext2,
            parentValue,
            readOnly,
            resetKeys,
        ]);

        if (hidden) return null;

        const rootClasses = classnames('fm-crud-field', 'fm-field-multiple-fuzzy-single', {
            [`fm-field-multiple-fuzzy-single__${fieldId}`]: !!fieldId,
        });

        return (
            <div ref={containerRef} className={rootClasses}>
                {fields}
                {addButton}
            </div>
        );
    },
);

MultipleFuzzySingle.displayName = 'MultipleFuzzySingle';

MultipleFuzzySingle.propTypes = {
    data: PropTypes.object,
    disableButtonOnBulk: PropTypes.bool,
    environmentField: PropTypes.object,
    errors: PropTypes.object,
    fieldsToClear: PropTypes.object,
    fieldId: PropTypes.string,
    firstErrorField: PropTypes.string,
    getError: PropTypes.func,
    getFieldsActions: PropTypes.func,
    hidden: PropTypes.bool,
    inputs: PropTypes.array,
    isBulkAction: PropTypes.bool,
    labelButton: PropTypes.string,
    labelMode: PropTypes.string,
    max: PropTypes.number,
    menuProps: PropTypes.object,
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    parentField: PropTypes.string,
    parentFieldForContext: PropTypes.string,
    parentFieldForContext2: PropTypes.string,
    parentValue: PropTypes.any,
    readOnly: PropTypes.bool,
    smartFieldOverridesCache: PropTypes.object,
};

export default MultipleFuzzySingle;
